import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { useIntercom } from "react-use-intercom";
import { useUserContext } from "@/contexts/UserContext";
import { API } from "@/lib/api";
import { ErrorCode } from "@boxem/core-api-schema";
import TextInput from "@/components/forms/TextInput/TextInput";
import Button from "@/components/forms/Button/Button";
import Flex from "@/components/Flex/Flex";
import Checklist from "@/components/Checklist/Checklist";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Link from "@/components/Link/Link";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import { logRocketError } from "@/utils/logUtils";

import styles from "./signup-form.module.css";

type SignupForm = {
  name: string;
  email: string;
  confirmEmail: string;
  password: string;
  repeat: string;
  captcha: string;
};

export default function SignupForm(props: { xid?: string }) {
  const [location, navigate] = useLocation();
  const searchParams = new URLSearchParams(useSearch());
  const [submitButtonLoading, setSubmitButtonLoading] = useState<boolean>(false);
  const plan = searchParams.get("plan");
  const intercom = useIntercom();
  const { setUser } = useUserContext();
  const { showNotification } = useNotificationContext();
  const [fullNameErrorText, setFullNameErrorText] = useState<string>("");
  const [emailErrorText, setEmailErrorText] = useState<string>("");
  const [confirmEmailErrorText, setConfirmEmailErrorText] = useState<string>("");
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [repeatErrorText, setRepeatErrorText] = useState<string>("");
  const [captchaErrorText, setCaptchaErrorText] = useState<string>("");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);

  // @NOTE(nick) this has to be separate from the signup form var,
  // because otherwise the callback for rendering the captcha element
  // will capture the wrong (initial) value of the signup form, so when
  // we do onChange setSignupForm({...signupForm, captcha }), we clear
  // the form :(
  const [captchaText, setCaptchaText] = useState<string>("");
  const [signupForm, setSignupForm] = useState({
    name: "",
    email: "",
    confirmEmail: "",
    password: "",
    repeat: "",
    captcha: "", // will be set by side-effect of setting captchaText
  } as SignupForm);

  //Validate all the inputs - used for validation on submit
  const validateInputs = () => {
    let valid = true;

    //Fullname rules
    if (signupForm.name === "") {
      setFullNameErrorText("Please enter your full name.");
      valid = false;
    }

    //Email rules
    if (signupForm.email === "") {
      setEmailErrorText("Email address is required.");
      valid = false;
    }
    if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(signupForm.email)) {
      setEmailErrorText("Invalid email address.");
      valid = false;
    }

    // confirm email
    if (signupForm.confirmEmail !== signupForm.email) {
      setConfirmEmailErrorText("Emails do not match.");
      valid = false;
    }

    //Password rules
    if (signupForm.password === "") {
      setPasswordErrorText("Password is required.");
      valid = false
    }
    if (signupForm.password?.length < 8 || signupForm.password?.length > 64) {
      setPasswordErrorText("Password must be between 8 and 64 characters.");
      valid = false
    }

    //Repeat rules
    if (signupForm.repeat === "") {
      setRepeatErrorText("Please confirm your password.");
      valid = false;
    }
    if (signupForm.repeat !== signupForm.password) {
      setRepeatErrorText("Password mismatch.");
      valid = false;
    }

    // captcha
    if (!signupForm.captcha || signupForm.captcha === "") {
      setCaptchaErrorText("Captcha is not solved")
      valid = false;
    }

    //If the form was invalid then disable the submit button.
    if (!valid) {
      setSubmitButtonDisabled(true);
    }

    return valid;
  };

  //Submit form values
  const handleSubmit = async () => {
    setSubmitButtonLoading(true);

    const inputsAreValid = validateInputs();

    //If inputs aren't valid then don't continue with submit.
    if (!inputsAreValid) {
      setSubmitButtonLoading(false);
      return;
    }

    try {
      signupForm.email = signupForm.email.toLowerCase();
      const user = await API.signup({...signupForm, xid: props.xid });
      setUser(user, false);

      window.LogRocket?.identify(user.uuid, {
        name: signupForm.name,
        email: signupForm.email,
        plan,
      });

      intercom.update({
        name: signupForm.name,
        email: signupForm.email,
        userId: user.uuid,
      });

      showNotification("Account has been registered.\nPlease enter your billing info to start your free trial.", NotificationType.SUCCESS);

      navigate(`~/auth/verify-email?plan=${plan}`);

      // setTimeout(() => {
      //   // Set timeout before redirect here because of all the tracking scripts that need to fire off.
      //   // Might not be necessary but just in case.
      //   // Feels bad. -Michael
      //   navigate(`~/auth/payment-information?plan=${plan}`);
      // }, 100);
    } catch (e: any) {
      if (Array.isArray(e) && e[0]?.type === ErrorCode.SIGNUP_ACCOUNT_EMAIL_ALREADY_EXISTS) {
        showNotification("Account with this email already exists!", NotificationType.ERROR);

      } else {
        showNotification("Failed to sign up!", NotificationType.ERROR);
        console.error("==== Failed to sign up\n", e);
      }
    } finally {
      setSubmitButtonLoading(false);
    }
  };

  //Handle full name change
  const onFullNameChange = (name: string) => {
    setSignupForm({...signupForm, name });
    setFullNameErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle email change
  const onEmailChange = (email: string) => {
    setSignupForm({...signupForm, email });
    setEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle email change
  const onConfirmEmailChange = (confirmEmail: string) => {
    setSignupForm({...signupForm, confirmEmail });
    setConfirmEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle password change
  const onPasswordChange = (password: string) => {
    setSignupForm({...signupForm, password });
    setPasswordErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle repeat change
  const onRepeatChange = (repeat: string) => {
    setSignupForm({...signupForm, repeat });
    setRepeatErrorText("");
    setSubmitButtonDisabled(false);
  };

  const grecaptchaCallback = (captcha: string) => {
    setCaptchaText(captcha);
    setCaptchaErrorText("");
    setSubmitButtonDisabled(false);
  }

  // render/setup the captcha element
  useEffect(() => {
    const captchaElement = document.querySelector(".g-recaptcha");
    if (!captchaElement) {
      logRocketError(`Failed to find element to host captcha!`);
      return;
    }
    // @ts-ignore
    if (!window.grecaptcha || !window.grecaptcha.render) {
      logRocketError(`grecaptcha not loaded when sign-up page renders!`);
      return;
    }

    // @ts-ignore
    grecaptcha?.render(
      captchaElement,
      {
        sitekey: "6LeK70ErAAAAAJVXnOS3op9O1KdVNpmq30b6ZV4i",
        callback: grecaptchaCallback, // this needs to be defined above, otherwise it will capture the wrong values.
        "expired-callback": () => {
          // happens when you solve a captcha, then wait ~1 minute. we need to invalidate the captcha
          setCaptchaText("");
        },
        "error-callback": (e) => {
          logRocketError(`Error solving captcha: ${e}`);
          setCaptchaText("");
        }
      }
    );

  }, [])

  useEffect(() => {
    setSignupForm({...signupForm, captcha: captchaText });
  }, [captchaText]);

  //Cleanup form once comp is unmounted
  useEffect(() => {
    return () => {
      setSignupForm({
        name: "",
        email: "",
        confirmEmail: "",
        password: "",
        repeat: "",
        captcha: "",
      });
    };
  }, []);

  return (
    <div className={styles.formContainer}>
      <div className={styles.inputGroup}>
        <label htmlFor="fullName">
          <Flex gap={1.5} align="center">
            <span>Full name</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="text"
            autocomplete="name"
            inputAutofocus={true}
            inputId="fullName"
            placeholder="ex: John Doe"
            value={signupForm.name}
            onChange={onFullNameChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {fullNameErrorText && <p className={`small-text ${styles.invalidText}`}>{fullNameErrorText}</p>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="email">
          <Flex gap={1.5} align="center">
            <span>Email address</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="email"
            autocomplete="email"  
            inputId="email"
            placeholder="your@email.com"
            value={signupForm.email}
            onChange={onEmailChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {emailErrorText && <p className={`small-text ${styles.invalidText}`}>{emailErrorText}</p>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="confirmEmail">
          <Flex gap={1.5} align="center">
            <span>Confirm email address</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="email"
            autocomplete="email"
            inputId="confirmEmail"
            placeholder="your@email.com"
            value={signupForm.confirmEmail}
            onChange={onConfirmEmailChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {confirmEmailErrorText && <p className={`small-text ${styles.invalidText}`}>{confirmEmailErrorText}</p>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="password">
          <Flex gap={1.5} align="center">
            <span>Password</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="password"
            autocomplete="new-password"
            inputId="password"
            placeholder="Your password"
            value={signupForm.password}
            onChange={onPasswordChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {passwordErrorText && <p className={`small-text ${styles.invalidText}`}>{passwordErrorText}</p>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="repeat">
          <Flex gap={1.5} align="center">
            <span>Repeat password</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="password"
            autocomplete="new-password"
            inputId="repeat"
            placeholder="Repeat your password"
            value={signupForm.repeat}
            onChange={onRepeatChange}
            onSubmit={handleSubmit}
          />
        </Flex>

        {repeatErrorText && <p className={`small-text ${styles.invalidText}`}>{repeatErrorText}</p>}
      </div>

      <div className={styles.checkListContainer}>
        <Checklist
          color="success"
          items={[
            { label: "Should be at least 8 characters", checked: signupForm.password?.length >= 8 },
          ]}
        />
      </div>

      <Flex className={styles.captchaWrapper} direction="column" justify="center" align="center">
        <div className="g-recaptcha"></div>
        {captchaErrorText && <p className={`small-text ${styles.invalidText}`}>{captchaErrorText}</p>}
      </Flex>

      <div>
        <div>
          By signing up you are agreeing to our <a href="https://www.boxem.com/termsandconditions" target="_blank">Terms & Conditions</a> and
          have read through our <a href="https://www.boxem.com/privacypolicy" target="_blank">Privacy Policy</a>.
        </div>

        <div className={styles.space}></div>

        <Button
          type="primary"
          disabled={submitButtonLoading || submitButtonDisabled}
          className={styles.submitButton}
          onClick={handleSubmit}
        >
          {submitButtonLoading ? <LoadingSpinner /> : <span>Continue to payment</span>}
        </Button>

        <div className={styles.space}></div>

        <div className={styles.navigate}>
          Already have an account? <Link href="/auth/login">Log in</Link>
        </div>
        <div className={styles.space}></div>

        <div className={styles.navigate}>
          <Link href="https://www.boxem.com/pricing">Return to Pricing Page</Link>
        </div>
      </div>
    </div>
  );
}
